import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconMail, IconLink, IconDownload, IconBan, IconRefresh, IconArrowLeft, IconSignature } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { SignatureInput, exportDrawn, renderTyped } from '../sign/signatureTools';
import { callFn, openContractFile, StatusChip, fmtDateTime, fmtDate, fmtMoney, inputStyle, CONSENT_TEXT } from './contractsApi';

const REGEN_WARNING = 'This creates a new link for this signer. The previous link will stop working immediately. Continue?';

export default function ContractDetail() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [signingFor, setSigningFor] = useState(null);
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();

  const load = useCallback(() => {
    callFn('getContract', { contractId }).then(setData).catch(e => setError(e.message));
  }, [contractId]);
  useEffect(load, [load]);

  const run = async (key, fn) => {
    setActing(key); setError(null); setNotice(null);
    try { await fn(); } catch (e) { setError(e.message); }
    setActing(null);
    load();
  };

  const invite = (s, viaEmail) => {
    if (s.linkIssued && !window.confirm(REGEN_WARNING)) return;
    run(`${s.id}:${viaEmail ? 'email' : 'copy'}`, async () => {
      const res = await callFn('sendInvite', { contractId, signerId: s.id, sendEmail: viaEmail });
      if (viaEmail) {
        setNotice(`Invite emailed to ${s.email}.`);
      } else {
        try {
          await navigator.clipboard.writeText(res.link);
          setNotice(`Signing link for ${s.name} copied to the clipboard. It will not be shown again.`);
        } catch {
          window.prompt('Copy this signing link. It will not be shown again.', res.link);
        }
      }
    });
  };

  const voidIt = () => {
    if (!window.confirm('Void this contract? Every signing link will stop working. This cannot be undone.')) return;
    const notify = window.confirm('Email the signers who have not signed yet to tell them the contract was withdrawn?');
    run('void', async () => {
      const r = await callFn('voidContract', { contractId, notifySigners: notify });
      setNotice(notify ? `Contract voided. ${r.notified} signer(s) notified.` : 'Contract voided.');
    });
  };

  const paymentLink = (viaEmail) => {
    if (data.contract.payment.linkIssued && !window.confirm('This creates a new payment link. The previous payment link will stop working. Continue?')) return;
    run(`pay:${viaEmail ? 'email' : 'copy'}`, async () => {
      const res = await callFn('sendPaymentLink', { contractId, sendEmail: viaEmail });
      if (viaEmail) {
        setNotice('Payment link emailed to the payer.');
      } else {
        try {
          await navigator.clipboard.writeText(res.link);
          setNotice('Payment link copied to the clipboard. It will not be shown again.');
        } catch {
          window.prompt('Copy this payment link. It will not be shown again.', res.link);
        }
      }
    });
  };

  const sendAll = () => run('all', async () => {
    const targets = data.signers.filter(s => s.id !== data.contract.senderSignerId && s.status === 'waiting'
      && (data.contract.signingOrder !== 'sequential' || s.isNext));
    const failed = [];
    for (const s of targets) {
      try { await callFn('sendInvite', { contractId, signerId: s.id, sendEmail: true }); } catch (e) { failed.push(`${s.name}: ${e.message}`); }
    }
    if (failed.length) throw new Error(`Some invites failed. ${failed.join(' ')}`);
    setNotice(`Invite${targets.length === 1 ? '' : 's'} emailed to ${targets.map(s => s.email).join(', ')}.`);
  });

  const retry = () => run('retry', async () => {
    await callFn('retryFinalise', { contractId });
    setNotice('Contract finalised and emailed.');
  });

  if (!data) {
    return (
      <Layout>
        <PageHeader title="Contract" subtitle={error || 'Loading...'} />
      </Layout>
    );
  }

  const { contract: c, signers } = data;
  const open = ['draft', 'pending'].includes(c.status) && !c.expired;
  const sender = signers.find(s => s.id === c.senderSignerId);
  const senderSigned = !sender || sender.status === 'signed';
  const clientSignerCount = signers.filter(s => s.id !== c.senderSignerId).length;
  const uninvited = signers.filter(s => s.id !== c.senderSignerId && s.status === 'waiting' && (c.signingOrder !== 'sequential' || s.isNext));

  return (
    <Layout>
      <PageHeader
        title={c.title}
        subtitle={`${c.clientName} · ${c.signedCount} of ${c.signerCount} signed${clientSignerCount > 1 ? ` · ${c.signingOrder === 'sequential' ? 'Sequential' : 'Parallel'} signing` : ''}`}
        actions={<>
          <Button variant="ghost" icon={<IconArrowLeft size={14} stroke={1.5} />} onClick={() => navigate('/contracts')}>Contracts</Button>
          {open && <Button variant="danger" loading={acting === 'void'} icon={<IconBan size={14} stroke={1.5} />} onClick={voidIt}>Void</Button>}
        </>}
      />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
        {error && <Banner kind="danger">{error}</Banner>}
        {notice && <Banner kind="success">{notice}</Banner>}
        {open && sender && !senderSigned && !signingFor && (
          <Banner kind="warning">
            Next step: sign the contract yourself. The invites unlock once you have signed.
            <div style={{ marginTop: 8 }}><Button size="sm" icon={<IconSignature size={12} stroke={2} />} onClick={() => setSigningFor(sender)}
              disabled={sender.email !== myEmail}>Sign now</Button>
              {sender.email !== myEmail && <span style={{ marginLeft: 8 }}>Only {sender.email} can sign here.</span>}
            </div>
          </Banner>
        )}
        {open && senderSigned && uninvited.length > 0 && (
          <Banner kind="info">
            {c.status === 'draft' ? 'You have signed. Ready to send.' : 'Some signers have not been invited yet.'}
            <div style={{ marginTop: 8 }}>
              <Button size="sm" loading={acting === 'all'} icon={<IconMail size={12} stroke={2} />} onClick={sendAll}>
                {uninvited.length === 1 ? `Email invite to ${uninvited[0].name}` : `Email all ${uninvited.length} invites`}
              </Button>
            </div>
          </Banner>
        )}
        {c.status === 'finalising' && (
          <Banner kind="warning">
            All parties have signed. The completed PDF is being generated. If this does not clear within a minute, finalisation may have failed.
            <div style={{ marginTop: 8 }}><Button size="sm" loading={acting === 'retry'} icon={<IconRefresh size={12} stroke={2} />} onClick={retry}>Retry finalisation</Button></div>
          </Banner>
        )}
        {c.completionEmailFailures?.length > 0 && <Banner kind="warning">The completed PDF could not be emailed to: {c.completionEmailFailures.join(', ')}</Banner>}

        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, fontSize: 12 }}>
            <Info label="Status"><StatusChip status={c.expired && c.status === 'pending' ? 'expired' : c.status} /></Info>
            <Info label="Created">{fmtDateTime(c.createdAt)}<div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{c.createdBy}</div></Info>
            {open && <Info label="Expires">{c.expiresAt ? fmtDate(c.expiresAt) : 'Set when first invite is sent'}</Info>}
            <Info label="Completed">{fmtDateTime(c.completedAt)}</Info>
            <Info label="Original SHA-256" span={4}><code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, wordBreak: 'break-all' }}>{c.originalSha256}</code></Info>
            {c.completedSha256 && <Info label="Completed SHA-256" span={4}><code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, wordBreak: 'break-all' }}>{c.completedSha256}</code></Info>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Button size="sm" variant="secondary" icon={<IconDownload size={12} stroke={2} />} onClick={() => openContractFile(contractId, 'original')}>Original PDF</Button>
            {c.clientProjectId && <Button size="sm" variant="secondary" onClick={() => navigate(`/projects/${c.clientProjectId}`)}>Client project {c.clientProjectId}</Button>}
            {c.completedPath && <Button size="sm" icon={<IconDownload size={12} stroke={2} />} onClick={() => openContractFile(contractId, 'completed')}>Completed PDF</Button>}
          </div>
        </Card>

        {signingFor && (
          <SignNowCard contractId={contractId} signer={signingFor}
            onCancel={() => setSigningFor(null)}
            onSigned={(completed) => {
              setSigningFor(null);
              setNotice(completed ? 'Signed. All parties have now signed, and the completed PDF has been emailed.' : 'Your signature is recorded. You can now send the invites.');
              load();
            }} />
        )}

        {c.payment && (
          <PaymentCard payment={c.payment} contract={c} payer={signers.find(s => s.id === c.payment.payerSignerId)}
            acting={acting} onLink={paymentLink} />
        )}

        <Card padding={0}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 90px 130px 130px 300px', padding: '10px 16px', gap: 12, fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
            <div>Signer</div><div>Status</div><div>Last viewed</div><div>Signed</div><div />
          </div>
          {signers.map((s, i) => {
            const blocked = c.signingOrder === 'sequential' && !s.isNext;
            const isSender = s.id === c.senderSignerId;
            const canAct = open && s.status !== 'signed' && !blocked && (isSender || senderSigned);
            const lastView = s.views?.length ? s.views[s.views.length - 1].at : null;
            const lastInvite = s.inviteSentAt?.length ? s.inviteSentAt[s.inviteSentAt.length - 1] : null;
            return (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '1.4fr 90px 130px 130px 300px', padding: '12px 16px', gap: 12, alignItems: 'center', fontSize: 12, borderBottom: i === signers.length - 1 ? 'none' : '0.5px solid var(--color-border-light)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{c.signingOrder === 'sequential' && `${s.order + 1}. `}{s.name}</div>
                  <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{s.role} · {s.email}</div>
                  {lastInvite && <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>Link issued {fmtDateTime(lastInvite.at)} ({lastInvite.method === 'copied' ? 'copied' : 'emailed'}), {s.inviteSentAt.length} total</div>}
                  {s.inviteError && <div style={{ color: 'var(--color-danger)', fontSize: 11 }}>Automatic invite email failed. Resend it.</div>}
                </div>
                <div><StatusChip status={s.status} /></div>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>{fmtDateTime(lastView)}</div>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: 11 }}>{fmtDateTime(s.signedAt)}</div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {canAct && (isSender || (!c.senderSignerId && s.email === myEmail)) && !signingFor && (
                    <Button size="sm" icon={<IconSignature size={12} stroke={2} />} onClick={() => setSigningFor(s)}>Sign now</Button>
                  )}
                  {canAct && !isSender && (c.senderSignerId || s.email !== myEmail) && <>
                    <Button size="sm" loading={acting === `${s.id}:email`} icon={<IconMail size={12} stroke={2} />} onClick={() => invite(s, true)}>
                      {s.linkIssued ? 'Resend' : 'Send invite'}
                    </Button>
                    <Button size="sm" variant="secondary" loading={acting === `${s.id}:copy`} icon={<IconLink size={12} stroke={2} />} onClick={() => invite(s, false)}>Copy link</Button>
                  </>}
                  {open && !isSender && s.status !== 'signed' && !senderSigned && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Sign first to send</span>}
                  {open && s.status !== 'signed' && blocked && senderSigned && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Waiting for earlier signers</span>}
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    </Layout>
  );
}

function Info({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Banner({ kind, children }) {
  return (
    <div style={{ padding: '10px 12px', background: `var(--color-${kind}-light)`, color: `var(--color-${kind})`, borderRadius: 'var(--radius-md)', fontSize: 12 }}>
      {children}
    </div>
  );
}

function PaymentCard({ payment: p, contract: c, payer, acting, onLink }) {
  const dash = `https://dashboard.stripe.com/${p.livemode === false ? 'test/' : ''}`;
  const canLink = payer?.status === 'signed' && c.status !== 'voided' && p.status !== 'paid';
  const links = [
    p.stripeCustomerId && ['Customer', `${dash}customers/${p.stripeCustomerId}`],
    p.paymentIntentId && ['Payment', `${dash}payments/${p.paymentIntentId}`],
    p.subscriptionId && ['Subscription', `${dash}subscriptions/${p.subscriptionId}`],
    p.invoiceId && ['Invoice', `${dash}invoices/${p.invoiceId}`],
  ].filter(Boolean);
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>Payment</span>
            <StatusChip status={`pay_${p.status}`} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 6 }}>{p.terms}</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            Payer: {payer ? `${payer.name} (${payer.email})` : 'Unknown'}
            {p.status === 'awaiting_signature' && ' · Payment link is sent automatically when they sign'}
          </div>
        </div>
        {canLink && (
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" loading={acting === 'pay:email'} icon={<IconMail size={12} stroke={2} />} onClick={() => onLink(true)}>
              {p.linkIssued ? 'Resend payment link' : 'Send payment link'}
            </Button>
            <Button size="sm" variant="secondary" loading={acting === 'pay:copy'} icon={<IconLink size={12} stroke={2} />} onClick={() => onLink(false)}>Copy link</Button>
          </div>
        )}
      </div>

      {p.lastError && (
        <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12 }}>{p.lastError}</div>
      )}

      <div style={{ marginTop: 14, borderTop: '0.5px solid var(--color-border-light)' }}>
        {p.instalments.map(i => (
          <div key={i.n} style={{ display: 'grid', gridTemplateColumns: '160px 120px 90px 1fr', gap: 12, alignItems: 'center', fontSize: 12, padding: '9px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
            <div>{p.plan === 'full' ? 'Payment in full' : `Instalment ${i.n} of 3`}</div>
            <div>{fmtMoney(i.amount, p.currency)}</div>
            <div><StatusChip status={`inst_${i.status}`} /></div>
            <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>
              {i.paidAt ? `Paid ${fmtDateTime(i.paidAt)}` : ''}
              {i.hostedInvoiceUrl && <> · <a href={i.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand)' }}>Invoice{i.number ? ` ${i.number}` : ''}</a></>}
            </div>
          </div>
        ))}
      </div>

      {links.length > 0 && (
        <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 12 }}>
          <span style={{ color: 'var(--color-text-tertiary)' }}>Stripe:</span>
          {links.map(([label, href]) => <a key={label} href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand)' }}>{label}</a>)}
        </div>
      )}
    </Card>
  );
}

function SignNowCard({ contractId, signer, onCancel, onSigned }) {
  const [name, setName] = useState(signer.name);
  const [mode, setMode] = useState('draw');
  const [typedSig, setTypedSig] = useState(signer.name);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const padRef = useRef(null);
  const sigOk = mode === 'draw' ? hasDrawn : typedSig.trim().length > 0;
  const canSubmit = name.trim() && sigOk && consent && !busy;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const res = await callFn('signAsSender', {
        contractId, signerId: signer.id, typedName: name.trim(),
        signaturePng: mode === 'draw' ? exportDrawn(padRef.current) : renderTyped(typedSig.trim()),
        signatureMethod: mode === 'draw' ? 'drawn' : 'typed', consent: true,
      });
      onSigned(res.completed);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Card>
      <div style={{ fontSize: 14 }}>Sign as {signer.name}, {signer.role}</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', margin: '4px 0 14px' }}>
        Your HQ login ({signer.email}) is recorded as your identity in the audit trail.{' '}
        <span onClick={() => openContractFile(contractId, 'original')} style={{ color: 'var(--color-brand)', cursor: 'pointer' }}>Review the PDF</span>
      </div>
      <div style={{ maxWidth: 520 }}>
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 5 }}>Full name</div>
        <input style={{ ...inputStyle, marginBottom: 14 }} value={name} onChange={e => setName(e.target.value)} />
        <SignatureInput mode={mode} setMode={setMode} typedSig={typedSig} setTypedSig={setTypedSig}
          padRef={padRef} onDrawnChange={setHasDrawn} inputStyle={inputStyle} />
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12, margin: '14px 0', cursor: 'pointer', lineHeight: 1.5 }}>
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
          {CONSENT_TEXT}
        </label>
        {error && <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button loading={busy} disabled={!canSubmit} icon={<IconSignature size={14} stroke={1.5} />} onClick={submit}>Sign</Button>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        </div>
      </div>
    </Card>
  );
}
